import React from 'react';
import { FinishData } from 'shared/types/product/Product';
import { ExtendedVariant } from 'shared/types/product/Variant';
import Prices from './prices';

interface PriceSectionProps {
  variant: ExtendedVariant;
  finish?: FinishData;
  pdpType?: 'custom' | 'standard';
  /**
    * Additional amount in cents to add to the base and discounted prices.
    * This can be used for finish/option-based price adjustments.
    */
  extraCentAmount?: number;
  /** Wrapper spacing; override when the caller lays the price out inline with other elements. */
  className?: string;
}

const PriceSection: React.FC<PriceSectionProps> = ({
  variant,
  finish,
  extraCentAmount,
  pdpType = 'standard',
  className = 'mb-24 mt-12',
}) => {
  const adjustment = typeof extraCentAmount === 'number' ? extraCentAmount : 0;
  const rawFinishCent = finish?.price ?? 0;
  const rawBaseCent = variant.price?.centAmount;
  const rawDiscountCent = variant.discountedPrice?.centAmount;
  const baseCent =
    typeof rawBaseCent === 'number' ? rawBaseCent + rawFinishCent : undefined;

  let discountFactor = 1;
  if (typeof rawBaseCent === 'number' && typeof rawDiscountCent === 'number' && rawBaseCent > 0) {
    discountFactor = rawDiscountCent / rawBaseCent;
  }

  const discountCent =
    typeof rawDiscountCent === 'number' ? rawDiscountCent + Math.round(adjustment * discountFactor) : 0;


  const hasPrice = typeof baseCent === 'number' && baseCent > 0;
  const hasDiscount =
    typeof discountCent === 'number' &&
    discountCent > 0 &&
    typeof baseCent === 'number' &&
    discountCent < baseCent;

  let discountPercentage: number | undefined;

  if (
    hasPrice &&
    hasDiscount &&
    typeof rawBaseCent === 'number' &&
    typeof rawDiscountCent === 'number'
  ) {
    discountPercentage = ((rawBaseCent - rawDiscountCent) / rawBaseCent) * 100;
  }

  if (!hasPrice || !variant.price) {
    return null;
  }

  const adjustedPrice = {
    ...variant.price,
    centAmount: baseCent as number,
  };

  const adjustedDiscountedPrice =
    hasDiscount && variant.discountedPrice
      ? {
        ...variant.discountedPrice,
        centAmount: discountCent as number,
      }
      : undefined;

  return (
    <div className={className}>
      <Prices
        price={adjustedPrice}
        discountedPrice={adjustedDiscountedPrice}
        discountPercentage={discountPercentage}
        pdpType={pdpType}
      />
    </div>
  );
};

export default PriceSection;
